// AE-facing "My Productivity" — built to the canonical design (design repo:
// docs/designs/2026-07-28-ae-productivity-v2/, Claude Design "AE Productivity v2").
// Self-scoped and historical: GET /dashboard/ae serves the signed-in AE without any capability,
// so this is the AE counterpart to the student's MyProductivity page.
//
// Two things the design draws have no data source yet and render as explicit "Not tracked yet"
// placeholders rather than fake numbers: attendance (lives in rrid-zoom-attendance), and an AE's
// own delivered pitch counts (the contract only knows queue depth, not pitches given).
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import {
  greenlitRatePct,
  preprintStatusLabel,
  NEXT_RANK_ACTIVATE_GREENLIT,
  NOT_PROPOSED_STATUSES,
  ON_HOLD_MIN_PROPOSED,
  ON_HOLD_UNCHECKED_RATIO,
  type AeDashboard,
  type DashboardPreprintCard,
  type ReviewerStatus,
} from "@rrid/shared-types";
import { api, ApiError } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { Badge, Card, CardBody, CardHeader, Input, Select, Spinner, statusTone } from "../../components/ui.js";
import { confirmAdvance } from "../preprints/advance-confirms.js";

const fmtDate = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

// Status bar / donut colours. Deliberately literal hex rather than `statusTone` badges: these are
// chart marks, and the badge palette has only six tones for more statuses than that.
const PIPELINE_COLORS: Record<string, string> = {
  "To Pitch(Editorial)": "#d97706",
  ACTIVE: "#1d4ed8",
  "ON HOLD": "#b45309",
  DORMANT: "#94a3b8",
  INVITED: "#0e7490",
  COMPLETE: "#15803d",
  DISCARDED: "#dc2626",
};
const OUTCOME_COLORS: Record<string, string> = {
  Invite: "#15803d",
  Unchecked: "#1d4ed8",
  Reject: "#dc2626",
  Fixable: "#d97706",
  Fixed: "#0e7490",
  "Double Check": "#7c3aed",
  "Student Reject": "#a16207",
  "COI Detected": "#be123c",
};
const outcomeColor = (s: string): string => OUTCOME_COLORS[s] ?? "#94a3b8";

export function AeProductivity(): JSX.Element {
  const [from, setFrom] = useState(() => fmtDate(Date.now() - 28 * 864e5));
  const [to, setTo] = useState(() => fmtDate(Date.now()));
  const weeks =
    from && to ? Math.max(0.5, Math.round(((Date.parse(to) - Date.parse(from)) / (7 * 864e5)) * 10) / 10) : null;

  const { data, isLoading } = useQuery({
    queryKey: [...qk.dashboardAe(), "productivity", from, to] as const,
    queryFn: () => api.get<AeDashboard>(`/dashboard/ae?${new URLSearchParams({ from, to })}`),
  });

  if (isLoading || !data) return <Spinner label="Loading your productivity…" />;

  const firstName = data.editor.name?.split(" ")[0];
  const pipeline = data.pipeline ?? [];
  const qc = data.qcDecisions ?? [];
  const completeCount = pipeline.filter((p) => p.status === "COMPLETE").length;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{firstName ? `${firstName}'s` : "My"} Productivity 👋</h1>
          <p className="mt-0.5 text-sm text-slate-500">An overview of your editorial work.</p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Badge tone="blue">Assistant Editor</Badge>
            {data.editor.team && <Badge tone="slate">Team {data.editor.team}</Badge>}
            {(data.editor.domains ?? []).length > 0 && <Badge tone="slate">{(data.editor.domains ?? []).join(", ")}</Badge>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-600">Period</span>
          <Input type="date" className="w-36 py-1" value={from} onChange={(e) => setFrom(e.target.value)} />
          <span className="text-slate-400">–</span>
          <Input type="date" className="w-36 py-1" value={to} onChange={(e) => setTo(e.target.value)} />
          <Badge tone="slate">{weeks ? `${weeks} weeks` : "All time"}</Badge>
        </div>
      </header>

      <SectionRule title="My work" note="Period totals · you only" />
      <MyWork data={data} completeCount={completeCount} qc={qc} />

      <PipelineBar preprints={data.preprints} pipeline={pipeline} />

      <div className="grid items-start gap-3 lg:grid-cols-[1.25fr_1fr]">
        <Shepherding data={data} pipeline={pipeline} />
        <ClosedOut pipeline={pipeline} />
      </div>

      <StudentsRoster students={data.students} outcomes={data.proposalOutcomes ?? []} />
    </div>
  );
}

function SectionRule({ title, note }: { title: string; note: string }): JSX.Element {
  return (
    <div className="flex items-baseline gap-2.5">
      <h2 className="shrink-0 text-xs font-bold uppercase tracking-widest text-slate-600">{title}</h2>
      <span className="h-px flex-1 bg-slate-200" />
      <span className="shrink-0 text-xs text-slate-400">{note}</span>
    </div>
  );
}

// ── My work ──────────────────────────────────────────────────────────────────
function MyWork({
  data,
  completeCount,
  qc,
}: {
  data: AeDashboard;
  completeCount: number;
  qc: NonNullable<AeDashboard["qcDecisions"]>;
}): JSX.Element {
  const k = data.kpis;
  const qcGreenlit = qc.filter((d) => d.status === "Invite").length;
  return (
    <Card>
      <CardBody className="grid gap-y-5 p-0 md:grid-cols-[1fr_0.62fr_1.3fr] md:divide-x md:divide-slate-100">
        <Panel title="Pitches" tone="text-blue-700">
          {/* An AE's delivered pitch counts aren't in any contract — `toPitchTeam`/`toPitchEditorial`
              are the queue of preprints awaiting a pitch, not pitches given. Shown as the queue it
              actually is, so the two aren't confused. */}
          <div className="grid grid-cols-3 gap-4">
            <Stat label="Awaiting pitch" value={data.preprints.toPitchTeam.length + data.preprints.toPitchEditorial.length} color="text-blue-700" />
            <Stat label="Selected" value={data.preprints.selected.length} color="text-cyan-700" />
            <Stat label="Complete" value={completeCount} color="text-green-700" />
          </div>
          <div className="flex gap-1.5">
            <Badge tone="blue">Team {data.preprints.toPitchTeam.length}</Badge>
            <Badge tone="amber">Editorial {data.preprints.toPitchEditorial.length}</Badge>
          </div>
          <p className="text-[11px] leading-snug text-slate-400">
            Pitches <em>delivered</em> aren&apos;t tracked yet — these are the preprints you lead that are queued for
            each gate right now.
          </p>
        </Panel>

        <Panel title="Attendance" tone="text-cyan-700">
          {/* No data source yet (attendance lives in rrid-zoom-attendance) — design parity without
              inventing numbers, matching the student page's placeholders. */}
          <div className="grid grid-cols-2 gap-4">
            <Stat label="Team" value="—" sub="Not tracked yet" />
            <Stat label="Editorial" value="—" sub="Not tracked yet" />
          </div>
        </Panel>

        <Panel title="Proposals" tone="text-violet-700">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Proposals" value={k.proposals} color="text-violet-700" sub="Truly proposed" />
            <Stat label="Greenlit" value={k.greenlitProposals} color="text-green-700" />
            <Stat label="QCs done" value={qc.length} sub="In period" />
            <Stat label="QCs greenlit" value={qcGreenlit} color="text-green-700" />
          </div>
        </Panel>
      </CardBody>
    </Card>
  );
}

function Panel({ title, tone, children }: { title: string; tone: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <div className={clsx("text-xs font-bold tracking-wide", tone)}>{title}</div>
      {children}
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  color = "text-slate-800",
}: {
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
}): JSX.Element {
  return (
    <div className="space-y-0.5">
      <div className="min-h-[26px] text-[11px] font-semibold uppercase leading-tight tracking-wide text-slate-500">
        {label}
      </div>
      <div className={clsx("text-[28px] font-bold leading-none", color)}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
    </div>
  );
}

// ── Led-preprint status bar ──────────────────────────────────────────────────
function PipelineBar({
  preprints,
  pipeline,
}: {
  preprints: AeDashboard["preprints"];
  pipeline: DashboardPreprintCard[];
}): JSX.Element {
  const [hovered, setHovered] = useState<string | null>(null);
  const segments = useMemo(() => {
    const counts = new Map<string, number>();
    const add = (status: string, n: number): void => {
      if (n > 0) counts.set(status, (counts.get(status) ?? 0) + n);
    };
    add("To Pitch(Editorial)", preprints.toPitchEditorial.length);
    add("ACTIVE", preprints.active.length);
    for (const p of pipeline) add(p.status, 1);
    // Keep the design's lifecycle order rather than sorting by size, so the bar reads left to right.
    const order = Object.keys(PIPELINE_COLORS);
    return [...counts.entries()]
      .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
      .map(([status, count]) => ({ status, count, color: PIPELINE_COLORS[status] ?? "#94a3b8" }));
  }, [preprints, pipeline]);

  const total = segments.reduce((n, s) => n + s.count, 0);
  const active = segments.find((s) => s.status === hovered) ?? null;

  if (total === 0) {
    return (
      <Card>
        <CardBody className="text-sm text-slate-500">You aren&apos;t leading any preprints yet.</CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex min-h-[18px] items-center gap-2">
          <span className="text-xs font-semibold text-slate-800">
            {active ? preprintStatusLabel(active.status) : "Led preprints by status"}
          </span>
          <span className="text-xs text-slate-400">
            {active
              ? `${active.count} of ${total} (${Math.round((active.count / total) * 100)}%)`
              : "Hover a segment for counts"}
          </span>
        </div>
        <div className="flex h-[22px] overflow-hidden rounded-md bg-slate-100" onMouseLeave={() => setHovered(null)}>
          {segments.map((s) => (
            <div
              key={s.status}
              title={`${preprintStatusLabel(s.status)}: ${s.count} of ${total} led preprints`}
              onMouseEnter={() => setHovered(s.status)}
              style={{
                width: `${(s.count / total) * 100}%`,
                background: s.color,
                opacity: hovered === null || hovered === s.status ? 1 : 0.45,
              }}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {segments.map((s) => (
            <div
              key={s.status}
              onMouseEnter={() => setHovered(s.status)}
              onMouseLeave={() => setHovered(null)}
              className="flex items-center gap-1.5"
            >
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: s.color }} />
              <span className="text-xs text-slate-600">{preprintStatusLabel(s.status)}</span>
              {/* Jul-29 revision: the legend carries its own count, so the bar is readable
                  without hovering every segment. */}
              <span className="text-xs font-semibold text-slate-400">{s.count}</span>
            </div>
          ))}
          <div className="ml-auto flex items-center gap-1.5 border-l border-slate-200 pl-4">
            <span className="text-xs font-semibold text-slate-600">Total led preprints</span>
            <span className="text-xs font-bold text-slate-800">{total}</span>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// ── Shepherding / Complete & discarded ───────────────────────────────────────
function riskCue(c: DashboardPreprintCard): { label: string; tone: "amber" | "green" | "slate" } | null {
  if (c.status !== "ACTIVE" && c.status !== "ON HOLD") return null;
  const proposed = c.proposedCount ?? 0;
  const greenlit = c.greenlitCount ?? 0;
  const ratio = proposed >= ON_HOLD_MIN_PROPOSED ? (c.uncheckedCount ?? 0) / proposed : 0;
  if (ratio >= ON_HOLD_UNCHECKED_RATIO) return { label: `Auto-hold risk · ${Math.round(ratio * 100)}% unchecked`, tone: "amber" };
  if (greenlit >= NEXT_RANK_ACTIVATE_GREENLIT) return { label: "Next rank activates", tone: "green" };
  return { label: `${greenlit} / ${NEXT_RANK_ACTIVATE_GREENLIT} to next activation`, tone: "slate" };
}

function PreprintRow({ card, actions }: { card: DashboardPreprintCard; actions: boolean }): JSX.Element {
  const risk = actions ? riskCue(card) : null;
  const href = card.selectedId ? `/preprints/${card.selectedId}` : null;
  const toPitchEditorial = card.status === "To Pitch(Editorial)";
  const counts =
    card.proposedCount != null || card.greenlitCount != null
      ? [
          { label: "Proposed", n: card.proposedCount ?? 0, tone: "violet" as const },
          { label: "Greenlit", n: card.greenlitCount ?? 0, tone: "green" as const },
          { label: "Unchecked", n: card.uncheckedCount ?? 0, tone: "amber" as const },
        ]
      : [];
  return (
    <div className="flex items-start gap-3 border-b border-slate-100 px-4 py-2.5 last:border-b-0">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={statusTone(card.status)}>{preprintStatusLabel(card.status)}</Badge>
          <span className="text-[13px] font-semibold leading-snug text-slate-800">
            {href ? (
              <Link to={href} className="hover:text-brand hover:underline">
                {card.title}
              </Link>
            ) : (
              card.title
            )}
          </span>
        </div>
        {card.publishedDate && <div className="text-[11px] text-slate-400">Published {card.publishedDate.slice(0, 10)}</div>}
        {counts.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {counts.map((c) => (
              <Badge key={c.label} tone={c.tone}>
                {c.label} <span className="font-bold">{c.n}</span>
              </Badge>
            ))}
          </div>
        )}
        {risk && (
          <div>
            <Badge tone={risk.tone}>{risk.label}</Badge>
          </div>
        )}
      </div>
      {actions && (
        <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
          {toPitchEditorial ? (
            card.selectedId ? (
              <>
                <SetToSelect selectedId={card.selectedId} />
                <RejectEditorial selectedId={card.selectedId} />
              </>
            ) : null
          ) : (
            <>
              <Link
                to="/quality-check"
                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
              >
                Quality check
              </Link>
              {card.preprintId && (
                <Link
                  to={`/reviewers?preprint=${card.preprintId}`}
                  className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  Propose reviewers
                </Link>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Same advance as AE home "Move to Selected" — label matches Softr/PRD "Set to Select(ed)". */
function SetToSelect({ selectedId }: { selectedId: string }): JSX.Element {
  const qc = useQueryClient();
  const advance = useMutation({
    mutationFn: () => api.post(`/preprints/selected/${selectedId}/advance`, { status: "Selected" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["selected"] });
    },
  });
  return (
    <span className="flex flex-col items-end gap-1">
      <button
        type="button"
        disabled={advance.isPending}
        onClick={() => {
          if (!confirmAdvance("Selected")) return;
          advance.mutate();
        }}
        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
      >
        Set to Select
      </button>
      {advance.isError && (
        <span className="max-w-56 text-right text-[11px] text-red-700">
          {advance.error instanceof ApiError ? advance.error.message : "Could not move this preprint."}
        </span>
      )}
    </span>
  );
}

/** Editorial-gate reject — same target as Find a Preprint / AE home for To Pitch(Editorial). */
function RejectEditorial({ selectedId }: { selectedId: string }): JSX.Element {
  const qc = useQueryClient();
  const advance = useMutation({
    mutationFn: () =>
      api.post(`/preprints/selected/${selectedId}/advance`, {
        status: "Editorial Rejected",
        aeChecks: { confirmed: true },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["selected"] });
    },
  });
  return (
    <span className="flex flex-col items-end gap-1">
      <button
        type="button"
        disabled={advance.isPending}
        onClick={() => {
          if (!window.confirm("Reject this preprint at the To Pitch(Editorial) gate?")) return;
          advance.mutate();
        }}
        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
      >
        Reject
      </button>
      {advance.isError && (
        <span className="max-w-56 text-right text-[11px] text-red-700">
          {advance.error instanceof ApiError ? advance.error.message : "Could not reject this preprint."}
        </span>
      )}
    </span>
  );
}

function Shepherding({ data, pipeline }: { data: AeDashboard; pipeline: DashboardPreprintCard[] }): JSX.Element {
  // Everything the AE is actively shepherding: queued for editorial, live, and the two
  // pre-activation states. Closed-out rows go to the panel beside this one.
  const OPEN = new Set(["ON HOLD", "DORMANT", "INVITED"]);
  const rows = [...data.preprints.toPitchEditorial, ...data.preprints.active, ...pipeline.filter((p) => OPEN.has(p.status))];
  return (
    <Card className="min-w-0">
      <CardHeader className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Shepherding</h3>
        <span className="text-xs text-slate-400">{rows.length} assigned to me</span>
      </CardHeader>
      {rows.length === 0 ? (
        <CardBody className="text-sm text-slate-500">Nothing in flight right now.</CardBody>
      ) : (
        <div className="flex flex-col">
          {rows.map((c) => (
            <PreprintRow key={c.preprintId ?? c.selectedId ?? c.title} card={c} actions />
          ))}
        </div>
      )}
    </Card>
  );
}

function ClosedOut({ pipeline }: { pipeline: DashboardPreprintCard[] }): JSX.Element {
  const rows = pipeline.filter((p) => p.status === "COMPLETE" || p.status === "DISCARDED");
  return (
    <Card className="min-w-0">
      <CardHeader className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Complete &amp; discarded</h3>
        <span className="text-xs text-slate-400">{rows.length} this term</span>
      </CardHeader>
      {rows.length === 0 ? (
        <CardBody className="text-sm text-slate-500">Nothing closed out yet.</CardBody>
      ) : (
        <div className="flex flex-col">
          {rows.map((c) => (
            <PreprintRow key={c.preprintId ?? c.selectedId ?? c.title} card={c} actions={false} />
          ))}
        </div>
      )}
    </Card>
  );
}

// ── My Students + proposal outcomes ──────────────────────────────────────────
type SortKey = "name" | "toPitchTeam" | "toPitchEditorial" | "proposals" | "greenlits" | "rate";

function StudentsRoster({
  students,
  outcomes,
}: {
  students: AeDashboard["students"];
  outcomes: NonNullable<AeDashboard["proposalOutcomes"]>;
}): JSX.Element {
  const [query, setQuery] = useState("");
  // Jul-29 revision: the domain/team selects are replaced by an activity filter (Active by
  // default, so deactivated students stop skewing the roster) and a greenlit-rate range.
  const [activity, setActivity] = useState<"Active" | "Inactive" | "All">("Active");
  const [rate, setRate] = useState<[number, number]>([0, 100]);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "proposals", dir: -1 });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const withRate = students
      .map((s) => ({
        ...s,
        rate:
          s.greenlitRate ??
          greenlitRatePct(s.greenlits, s.proposals, s.unchecked ?? 0),
      }))
      .filter((s) => activity === "All" || (s.active ?? true) === (activity === "Active"))
      .filter((s) => s.rate >= rate[0] && s.rate <= rate[1])
      .filter((s) => !q || `${s.name ?? ""} ${s.email}`.toLowerCase().includes(q));
    const { key, dir } = sort;
    return withRate.sort((a, b) =>
      key === "name"
        ? dir * (a.name ?? a.email).localeCompare(b.name ?? b.email)
        : dir * (((a[key] as number) ?? 0) - ((b[key] as number) ?? 0)),
    );
  }, [students, query, activity, rate, sort]);

  const toggle = (key: SortKey) => () => setSort((s) => ({ key, dir: s.key === key ? ((-s.dir) as 1 | -1) : -1 }));

  const NUM_COLS: { key: SortKey; label: string; title: string }[] = [
    { key: "toPitchTeam", label: "To Pitch (Team)", title: "Their pitches currently queued for a team meeting" },
    { key: "toPitchEditorial", label: "To Pitch (Editorial)", title: "Their pitches currently queued for editorial" },
    { key: "proposals", label: "Proposals", title: "Truly proposed (excludes Student Reject / COI Detected)" },
    { key: "greenlits", label: "Greenlits", title: "Proposals that reached Invite" },
    {
      key: "rate",
      label: "Greenlit rate",
      title: "Invite ÷ (proposed − Unchecked) — only QCd proposals in the denominator",
    },
  ];

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">👥 My Students</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            className="py-1"
            aria-label="Filter students by activity"
            value={activity}
            onChange={(e) => setActivity(e.target.value as "Active" | "Inactive" | "All")}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="All">All</option>
          </Select>
          <RateRange value={rate} onChange={setRate} />
          {/* Input is w-full by default, so it needs a sized wrapper — on its own wrapped line it
              would otherwise stretch the full width of the row. */}
          <span className="w-52">
            <Input
              type="search"
              placeholder="Search name or email…"
              className="py-1"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </span>
        </div>
      </div>

      <div className="grid items-start gap-3 xl:grid-cols-[minmax(0,1fr)_320px]">
        <Card className="min-w-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="bg-slate-50">
                  <Th onClick={toggle("name")} align="left">
                    Student
                  </Th>
                  {NUM_COLS.map((c) => (
                    <Th key={c.key} onClick={toggle(c.key)} title={c.title}>
                      {c.label}
                    </Th>
                  ))}
                  {/* Attendance has no source yet — headers kept so the shape matches the design. */}
                  {["Attend Team", "Attend Editorial", "Attend Class", "Attend Office hrs"].map((l) => (
                    <Th key={l} title="Not tracked yet">
                      {l}
                    </Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-6 text-center text-sm text-slate-500">
                      No students match these filters.
                    </td>
                  </tr>
                )}
                {rows.map((s) => (
                  <tr key={s.email} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <div className="flex flex-col gap-1">
                        <Link
                          to={`/my-productivity?student=${encodeURIComponent(s.email)}`}
                          className="whitespace-nowrap font-semibold text-brand hover:underline"
                          title={`Open ${s.name ?? s.email}'s Progress`}
                        >
                          {s.name ?? s.email}
                        </Link>
                        <span className="flex flex-wrap items-center gap-2">
                          <ActivityChip active={s.active ?? true} />
                          <span className="text-[11px] text-slate-400">
                            {[s.domains.join("/"), s.team].filter(Boolean).join(" · ") || "—"}
                          </span>
                        </span>
                      </div>
                    </td>
                    <Td>{s.toPitchTeam ?? 0}</Td>
                    <Td>{s.toPitchEditorial ?? 0}</Td>
                    <Td>{s.proposals}</Td>
                    <Td className="font-semibold text-green-700">{s.greenlits}</Td>
                    <Td className="font-semibold">{s.rate}%</Td>
                    <NotTracked />
                    <NotTracked />
                    <NotTracked />
                    <NotTracked />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100 px-4 py-2.5">
            <span className="text-xs text-slate-400">
              {rows.length} of {students.length} students · click a name for their Progress · click a column to sort.
              Pitch queues, proposals, greenlits, domain and team come from the dashboard contract; attendance columns
              have no source yet.
            </span>
          </div>
        </Card>

        <ProposalOutcomes outcomes={outcomes} />
      </div>
    </section>
  );
}

/** Two-handle greenlit-rate filter. The design draws one track with two pointer-dragged handles;
 *  this stacks two native range inputs over that track instead, so the control stays keyboard-
 *  operable and screen-reader labelled — only the thumbs take pointer events. */
function RateRange({
  value: [lo, hi],
  onChange,
}: {
  value: [number, number];
  onChange: (v: [number, number]) => void;
}): JSX.Element {
  const THUMB =
    "pointer-events-none absolute inset-0 h-5 w-full appearance-none bg-transparent " +
    "[&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 " +
    "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand " +
    "[&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 " +
    "[&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-brand";
  return (
    <div className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-2.5 py-1">
      <span className="whitespace-nowrap text-xs font-medium text-slate-500">Rate</span>
      <div className="relative h-5 w-32 shrink-0">
        <span className="absolute inset-x-0 top-2 h-1 rounded-full bg-slate-200" />
        <span
          className="absolute top-2 h-1 rounded-full bg-brand"
          style={{ left: `${lo}%`, width: `${hi - lo}%` }}
        />
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={lo}
          aria-label="Minimum greenlit rate"
          className={THUMB}
          onChange={(e) => onChange([Math.min(Number(e.target.value), hi), hi])}
        />
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={hi}
          aria-label="Maximum greenlit rate"
          className={THUMB}
          onChange={(e) => onChange([lo, Math.max(Number(e.target.value), lo)])}
        />
      </div>
      <span className="w-14 whitespace-nowrap text-right text-xs font-semibold tabular-nums text-slate-800">
        {lo}–{hi}%
      </span>
    </div>
  );
}

function Th({
  children,
  onClick,
  title,
  align = "right",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  title?: string;
  align?: "left" | "right";
}): JSX.Element {
  return (
    <th
      onClick={onClick}
      title={title}
      className={clsx(
        "border-b border-slate-200 px-2 py-2 align-bottom text-[11px] font-semibold uppercase leading-tight tracking-wide text-slate-500",
        align === "left" ? "pl-4 text-left" : "text-right",
        onClick && "cursor-pointer hover:text-slate-700",
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <td className={clsx("px-2 py-2 text-right tabular-nums text-slate-700", className)}>{children}</td>;
}

function ActivityChip({ active }: { active: boolean }): JSX.Element {
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium",
        active ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600",
      )}
    >
      <span className={clsx("h-1.5 w-1.5 rounded-full", active ? "bg-green-500" : "bg-slate-400")} />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function NotTracked(): JSX.Element {
  return (
    <td className="px-2 py-2 text-right text-slate-300" title="Not tracked yet">
      —
    </td>
  );
}

function ProposalOutcomes({ outcomes }: { outcomes: NonNullable<AeDashboard["proposalOutcomes"]> }): JSX.Element {
  const total = outcomes.reduce((n, o) => n + o.count, 0);
  // The proposed denominator excludes Student Reject / COI Detected (WO-7/21 Track B) — they never
  // reached quality check. They stay visible as slices; the rate below is computed without them.
  const proposedTotal = outcomes
    .filter((o) => !NOT_PROPOSED_STATUSES.includes(o.status as ReviewerStatus))
    .reduce((n, o) => n + o.count, 0);
  const greenlit = outcomes.find((o) => o.status === "Invite")?.count ?? 0;
  const unchecked = outcomes.find((o) => o.status === "Unchecked")?.count ?? 0;
  // Stefano: Invite ÷ (proposed − Unchecked).
  const rate = greenlitRatePct(greenlit, proposedTotal, unchecked);

  let acc = 0;
  const gradient =
    total > 0
      ? `conic-gradient(${outcomes
          .map((o) => {
            const start = (acc / total) * 100;
            acc += o.count;
            return `${outcomeColor(o.status)} ${start}% ${(acc / total) * 100}%`;
          })
          .join(", ")})`
      : undefined;

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Proposal outcomes</h3>
        <span className="text-xs text-slate-400">On my led preprints</span>
      </CardHeader>
      <CardBody className="flex flex-col items-center gap-3.5">
        {total === 0 ? (
          <p className="py-6 text-sm text-slate-500">No proposals on your led preprints yet.</p>
        ) : (
          <>
            {/* Stefano: greenlit rate as the top-level number. */}
            <div className="w-full rounded-lg border border-green-100 bg-green-50/70 px-3 py-2.5 text-center">
              <div className="text-3xl font-bold tabular-nums text-green-800">{rate}%</div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-green-700">
                Greenlit rate
              </div>
              <p className="mt-0.5 text-[11px] text-green-800/70">
                {greenlit} invite ÷ ({proposedTotal} proposed − {unchecked} unchecked)
              </p>
            </div>
            <div className="relative h-40 w-40 rounded-full" style={{ background: gradient }}>
              <div className="absolute inset-[30px] flex flex-col items-center justify-center gap-0.5 rounded-full bg-white">
                <span className="text-3xl font-bold leading-none text-slate-800">{proposedTotal}</span>
                <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Proposed</span>
              </div>
            </div>
            <div className="flex w-full flex-col gap-1.5">
              {outcomes.map((o) => (
                <div key={o.status} className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: outcomeColor(o.status) }} />
                  <span className="min-w-0 flex-1 truncate text-[13px] text-slate-700">{o.status}</span>
                  <span className="text-[13px] font-semibold tabular-nums text-slate-800">{o.count}</span>
                  <span className="w-9 text-right text-xs tabular-nums text-slate-400">
                    {Math.round((o.count / total) * 100)}%
                  </span>
                </div>
              ))}
            </div>
            <p className="text-[11px] leading-relaxed text-slate-400">
              Rate uses QCd proposals only (Invite ÷ proposed − Unchecked). Student Reject and COI
              Detected never reached quality check.
            </p>
          </>
        )}
      </CardBody>
    </Card>
  );
}
